import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { OrdersResult, OrderStatus } from '~/models/marketplace/order'
import { EshopProductsResult } from '~/models/marketplace/seller'
import { ReviewsResult } from '~/models/marketplace/reviews'
import { ActionResult } from '~/models/shared/result'
import {
  AdminSeller,
  AdminSellerValues,
  CategorySettingsRow,
  ReviewReport
} from '~/models/marketplace/admin'
import { Pagination } from '~/models/search/types'
import { DynamicFormSchema } from '~/models/dynamic-form/schema'
import { CartSellerInfo } from '~/models/marketplace/cart'
import { toCamelCase } from '~/utils/object'
import { arraySchemaToObj } from '~/utils/form'
import { toSnakeCase } from '~/utils/snake-case'
import { Log } from '~/models/log'

@containerScoped()
export default class MarketplaceAdminService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  getOrders(params: {
    pg?: number
    status?: OrderStatus
    'created-from'?: string
    'created-to'?: string
  }): Promise<OrdersResult> {
    return this.requestBuilder
      .request('get', `/api/market/admin/orders/`)
      .params(params)
      .send()
  }

  getOrderLogs(
    orderId: string | number,
    page = 1
  ): Promise<{ logs: { pagination: Pagination; rows: Log } }> {
    return this.requestBuilder
      .request('get', `/api/market/admin/order/${orderId}/logs/`)
      .params({ pg: page })
      .send()
  }

  getProducts(pg = 1): Promise<EshopProductsResult> {
    return this.requestBuilder
      .request('get', `/api/market/admin/products/`)
      .params({ pg })
      .send()
  }

  getProductLogs(
    productId: string | number,
    page = 1
  ): Promise<{ logs: { pagination: Pagination; rows: Log } }> {
    return this.requestBuilder
      .request('get', `/api/market/admin/product/${productId}/logs/`)
      .params({ pg: page })
      .send()
  }

  getReviews(pg = 1, showPublic: boolean = false): Promise<ReviewsResult> {
    return this.requestBuilder
      .request('get', `/api/admin/market/reviews/list/`)
      .params({ pg, non_public: (!showPublic).toString() })
      .send()
  }

  getSellers(params: {
    q?: string
  }): Promise<{ sellers: { rows: AdminSeller[]; pagination: Pagination } }> {
    return this.requestBuilder
      .request('get', `/api/market/admin/sellers/`)
      .params(params)
      .send()
  }

  getSellerLogs(
    sellerId: string | number,
    page = 1
  ): Promise<{ logs: { pagination: Pagination; rows: Log } }> {
    return this.requestBuilder
      .request('get', `/api/market/admin/seller/${sellerId}/logs/`)
      .params({ pg: page })
      .send()
  }

  approveReview(reviewId: number | string): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/admin/market/reviews/${reviewId}/approve/`)
      .send()
  }

  rejectReview(reviewId: number | string): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/admin/market/reviews/${reviewId}/reject/`)
      .send()
  }

  informSeller(reviewId: number | string): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/admin/market/reviews/${reviewId}/inform-seller/`)
      .send()
  }

  getReviewsReports(
    page: number
  ): Promise<{
    reports: { rows: ReviewReport[]; pagination: Pagination }
  }> {
    return this.requestBuilder
      .request('get', `/api/admin/market/reviews/reports/`)
      .params({ pg: page })
      .send()
  }

  getSellerInfo(
    sellerId: number
  ): Promise<{
    schema: DynamicFormSchema
    values: AdminSellerValues
    seller: CartSellerInfo
  }> {
    return this.requestBuilder
      .request('get', `/api/market/admin/seller/${sellerId}/`)
      .map(body =>
        toCamelCase({
          ...body.data,
          schema: arraySchemaToObj(body.data.schema)
        })
      )
      .send()
  }

  editSellerInfo(
    sellerId: number,
    form: AdminSellerValues
  ): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/market/admin/seller/${sellerId}/`)
      .data(toSnakeCase(form))
      .send()
  }

  getSettingsCategories(
    params: {
      category?: number | string
      q?: string
      pg?: number
    } = {}
  ): Promise<{
    categories: { pagination: Pagination; rows: CategorySettingsRow[] }
  }> {
    return this.requestBuilder
      .request('get', '/api/market/admin/settings/categories/')
      .params(params)
      .send()
  }

  setCategoryEnabled(
    categoryId: number,
    enabled: boolean
  ): Promise<ActionResult> {
    return this.requestBuilder
      .request('put', `/api/market/admin/settings/categories/${categoryId}/`)
      .data({ enabled })
      .send()
  }
}
